/**
 * Expands the "has any TechDocs reference" marker filter emitted by
 * `TechDocsEntityFilter` / `KindOrDocsEntityFilter` into the array (OR) form of
 * the catalog filter query.
 *
 * The catalog list provider merges every active filter into a single record
 * whose keys are AND-ed, so an "annotation A or annotation B" condition cannot
 * be expressed there. Rewriting the request here keeps the OR server-side, and
 * with it the offset pagination and total count the documentation view uses.
 *
 * Both `queryEntities` and `getEntityFacets` are patched: Lifecycle / Tags /
 * Namespace discover options through `filters.kind`, and the kind∪docs union
 * puts the same marker on that filter so facets must expand it too. Without
 * the facets patch the inert marker matched nothing and hid those pickers.
 *
 * This patches CatalogClient.prototype once at startup, the same way
 * catalogApiDedup does. The patches are independent and compose in either
 * import order.
 */
import { CatalogClient } from '@backstage/catalog-client';
import { expandTechDocsRefFilter } from './components/catalog/filters/TechDocsEntityFilter';

const originalQueryEntities = CatalogClient.prototype.queryEntities;
const originalGetEntityFacets = CatalogClient.prototype.getEntityFacets;

CatalogClient.prototype.queryEntities = function patchedQueryEntities(
  request?: Parameters<typeof originalQueryEntities>[0],
  options?: Parameters<typeof originalQueryEntities>[1],
): ReturnType<typeof originalQueryEntities> {
  // Cursor-based requests carry no filter; only the initial/offset requests do.
  if (request && 'filter' in request && request.filter) {
    const expanded = expandTechDocsRefFilter(request.filter as any);
    if (expanded !== request.filter) {
      return originalQueryEntities.call(
        this,
        { ...request, filter: expanded } as typeof request,
        options,
      );
    }
  }

  return originalQueryEntities.call(this, request, options);
};

CatalogClient.prototype.getEntityFacets = function patchedGetEntityFacets(
  request: Parameters<typeof originalGetEntityFacets>[0],
  options?: Parameters<typeof originalGetEntityFacets>[1],
): ReturnType<typeof originalGetEntityFacets> {
  if (request?.filter) {
    const expanded = expandTechDocsRefFilter(request.filter as any);
    if (expanded !== request.filter) {
      return originalGetEntityFacets.call(
        this,
        { ...request, filter: expanded } as typeof request,
        options,
      );
    }
  }

  return originalGetEntityFacets.call(this, request, options);
};
